export type Addons =
  | 'BILLING'
  | 'TEST_BILLING'
  | 'SUGGESTED_PROMPTS'
  | 'TAGS'
  | 'TABLE'
  | 'GEO'
  | 'LOOKER_STUDIO'
  | 'EXPORT_DATA'
  | 'SOURCES_STATS'
  | 'SOURCES_STATS_ADVANCED'
  | 'REPORTS'
  | 'CONTEXT_INSIGHTS'
  // Panel page `/insights` (competitive positioning + source brand coverage). Independent of
  // `CONTEXT_INSIGHTS`, which gates the Context analysis tab.
  | 'INSIGHTS'
  | 'API_KEYS'
  | 'ONLY_SUPERADMIN_FEATURE'
  | 'PAUSE_MODE'
  | 'CLAUDE_WEB'
  | 'EXPORT_LLM_RESPONSE'
  | 'GEO_REPORT'
  | 'IMPORT_FROM_CSV'
  | 'PROJECT_GROUPS'
  | 'PROMPT_GROUP'
  | 'AGENCY'
  /** Nowy model uprawnień: role GUEST, nadpisania per zasób, zakres projektów. */
  | 'USER_PERMISSIONS'
  | 'KEYWORDS_PAGE'
  | 'ANOMALY_DETECTION'
  | 'NEW_STATS'
  | 'AI_SIGNALS'
  | 'NEW_PROMPTS_STATS'
  | 'AI_VISIBILITY_REPORT'
  // Zakładka „Reports" w projekcie — pełny raport widoczności z filtrów (zakres dat + modele).
  // Osobny od `AI_VISIBILITY_REPORT`, który bramkuje decka generowanego przez LLM.
  | 'VISIBILITY_REPORT'
  // Koduje nazwy modeli w URL-ach żądań (patrz `shared/utils/modelUrlAlias`) — dla klientów, których
  // korporacyjny filtr sieciowy blokuje adresy ze słowem "gemini" i przez to gubią część statystyk.
  | 'MODEL_URL_ALIAS'
  // The "Ads" project tab — ads detected inside AI answers (ChatGPT + AI Overview).
  | 'ADS_STATS'
  // Zakładka „Prompt Analyst” w projekcie — przegląd pojedynczego promptu z limitem miesięcznym.
  | 'PROMPT_ANALYST'
  // Harmonogram monitoringu per projekt (codziennie / co tydzień): sekcja w dialogu edycji
  // projektu i w ostatnim kroku kreatora. Worker honoruje harmonogram niezależnie od addonu.
  | 'MANAGE_MONITORING_SCHEDULE'
  // Strona „Marketplace” (zakup addonów przez Paddle). Prototyp: zakupy wyłączone do czasu obsługi
  // checkoutu addonu w webhooku płatności.
  | 'MARKETPLACE'
  // The "Agency Hub" page — the agency's central view (prompt allocation, projects, client access).
  // Gated together with the OWNER/ADMIN role; USER never sees it. Stage 1: the page is an empty shell.
  | 'AGENCY_HUB'
  // Weryfikacja źródeł może zapłacić za stronę, której nasz fetcher nie czyta (blokada / pusta treść):
  // Jina, potem ScrapFly. Koszt idzie na właściciela projektu, który cytował stronę.
  | 'SOURCE_FETCH_ESCALATION'
  // Przycisk „Zapisana kopia” na widoku źródła: zapisany TEKST cytowanej strony. Osobny addon, wymagany
  // także od superadmina — pokazywanie cudzych treści czeka na opinię prawną.
  | 'SOURCE_PAGE_COPY';

export const allAddons: Addons[] = [
  'SUGGESTED_PROMPTS',
  'TAGS',
  'TABLE',
  'GEO',
  'LOOKER_STUDIO',
  'EXPORT_DATA',
  'SOURCES_STATS',
  'SOURCES_STATS_ADVANCED',
  'REPORTS',
  'CONTEXT_INSIGHTS',
  'INSIGHTS',
  'API_KEYS',
  'ONLY_SUPERADMIN_FEATURE',
  'PAUSE_MODE',
  'CLAUDE_WEB',
  'EXPORT_LLM_RESPONSE',
  'GEO_REPORT',
  'IMPORT_FROM_CSV',
  'PROJECT_GROUPS',
  'PROMPT_GROUP',
  'AGENCY',
  'USER_PERMISSIONS',
  'KEYWORDS_PAGE',
  'ANOMALY_DETECTION',
  'NEW_STATS',
  'AI_SIGNALS',
  'NEW_PROMPTS_STATS',
  'AI_VISIBILITY_REPORT',
  'VISIBILITY_REPORT',
  'MODEL_URL_ALIAS',
  'ADS_STATS',
  'PROMPT_ANALYST',
  'MANAGE_MONITORING_SCHEDULE',
  'MARKETPLACE',
  'AGENCY_HUB',
  'SOURCE_FETCH_ESCALATION',
  'SOURCE_PAGE_COPY',
];

/**
 * Opisy pokazywane przy checkboxach w adminie (`AdminUsersEditPage`). `Record` bez `Partial` celowo:
 * nowy addon nie skompiluje się, dopóki nie dostanie opisu.
 */
export const addonDescriptions: Record<Addons, string> = {
  BILLING: 'Self-serve billing: plan, payment method and invoices in Settings.',
  TEST_BILLING: 'Billing in Stripe test mode — internal/test accounts only.',
  SUGGESTED_PROMPTS: 'Adds the "Suggested prompts" page with LLM-generated prompt ideas.',
  TAGS: 'Prompt tagging + the "Tags" page and tag filters.',
  TABLE: 'Brands comparison table on the project dashboard.',
  GEO: 'Per-project country selection (geolocated querying).',
  LOOKER_STUDIO: 'Looker Studio integration + daily reports pushed to it.',
  EXPORT_DATA: 'CSV/XLSX export of stats and prompt data.',
  SOURCES_STATS: 'The "Sources" page — domains and URLs cited by the models.',
  SOURCES_STATS_ADVANCED: 'Extra breakdowns on Sources (details of domains, URLs and prompts).',
  REPORTS: 'PDF report download button on the project dashboard.',
  API_KEYS: 'API keys page in Settings — access to the public API.',
  CONTEXT_INSIGHTS: 'The "Context analysis" page — context/topic analysis of answers.',
  INSIGHTS: 'The "Insights" page — competitive positioning and source brand coverage.',
  ONLY_SUPERADMIN_FEATURE: 'Legacy flag — no longer gates anything in the app.',
  PAUSE_MODE: 'Pausing prompts; paused prompts free up their quota slot.',
  CLAUDE_WEB: 'Claude queried with web search enabled (every run).',
  EXPORT_LLM_RESPONSE: 'Export of raw LLM answers.',
  GEO_REPORT: 'The "GEO report" page (geolocated visibility report).',
  IMPORT_FROM_CSV: 'Bulk prompt import from a CSV file.',
  PROJECT_GROUPS: 'Grouping projects in the sidebar.',
  PROMPT_GROUP: 'Prompt groups + the "Prompts" page and grouping in Sources.',
  AGENCY: 'The "Agency stats" page — cross-project summary for agencies.',
  USER_PERMISSIONS:
    'Per-user permissions: GUEST role, per-resource overrides and per-project scope for guests.',
  KEYWORDS_PAGE:
    'The "Keywords" page + the whole new keywords approach: at most 15 brands auto-activated per project ' +
    '(the rest wait as INACTIVE) and brand grouping by aliases and shared domain, in stats too.',
  ANOMALY_DETECTION: 'Anomaly detection and incidents on the dashboard.',
  NEW_STATS: 'New dashboard/stats version (V2) instead of the legacy one.',
  AI_SIGNALS: 'The "AI signals" page + its integration.',
  NEW_PROMPTS_STATS: 'New stats columns in the prompts table.',
  AI_VISIBILITY_REPORT: 'LLM-generated visibility deck (generated in the worker).',
  VISIBILITY_REPORT: 'The "Reports" tab in a project — full visibility report from the filters.',
  MODEL_URL_ALIAS: 'Encodes model names in request URLs — for clients whose network filter blocks "gemini".',
  ADS_STATS: 'The "Ads" page — ads detected inside AI answers (ChatGPT + AI Overview).',
  PROMPT_ANALYST: 'The "Prompt Analyst" tab — a written review of one prompt (monthly quota).',
  MANAGE_MONITORING_SCHEDULE:
    'Per-project monitoring schedule (daily / weekly) in the project edit dialog and the wizard. ' +
    'Gates only the editing and viewing — the worker honours whatever schedule a project has.',
  MARKETPLACE: 'The "Marketplace" page (prototype) — addon tiles; purchases are disabled for now.',
  AGENCY_HUB:
    'The "Agency Hub" page: prompt allocation, project pause/start and client access for agencies. ' +
    'Implies USER_PERMISSIONS and PAUSE_MODE and lifts the paused-prompt cap (max paused = unlimited).',
  SOURCE_FETCH_ESCALATION:
    'Source verification pays for pages our fetcher cannot read (Jina, then ScrapFly). Costs are metered to the project owner.',
  SOURCE_PAGE_COPY:
    'The "Saved copy" button on a source page — the stored text of a cited page as our verification read it. ' +
    'Superadmins need it too; grant explicitly (legal review of showing third-party content pending).',
};

/**
 * The implications the ADMIN SAVE enforces (llm-common `IMPLIED_ADDONS`, narrowed to the hub rule —
 * the NEW_STATS rule applies only at sign-up / provisioning and must not lock anything here). This copy
 * only lets the admin page show (and lock) what will be granted along with a ticked addon.
 */
export const impliedAddons: Partial<Record<Addons, readonly Addons[]>> = {
  AGENCY_HUB: ['USER_PERMISSIONS', 'PAUSE_MODE'],
};

/** The addon (if any) among `selected` that implies `addon`. */
export const impliedBy = (addon: Addons, selected: readonly string[]): Addons | null =>
  (Object.keys(impliedAddons) as Addons[]).find(
    (implier) => selected.includes(implier) && impliedAddons[implier]?.includes(addon)
  ) ?? null;
